/**
 * Circular progress gauge — an arc with a caption underneath.
 *
 * Public interface: `percent` (and optional `value`). Everything else is optional.
 * Hides the traffic-light thresholds, the arc geometry, the clamping of
 * out-of-range values, the loading placeholder, and the accessible label.
 *
 * The tone colour is chosen in CSS from `data-tone`, so no colour value is
 * ever decided in JS (DESIGN_SYSTEM.md §2, tokens-first). The progress arc
 * uses an SVG stroke with round caps so the coloured ends read as complete.
 *
 * Props:
 *   percent   — 0–100, drives the arc and tone. Pass null/undefined while
 *               loading to show a placeholder.
 *   value     — optional centre readout (a count). When omitted, the centre
 *               shows `percent` with a % suffix (kit default).
 *   label     — caption under the number (default 'Compliance')
 *   size      — 'md' (default) | 'sm'
 *   className — extra class(es) on the root
 *
 * All other props pass through to the root element.
 */

/** Compliance traffic light. Shared so callers colour other UI consistently. */
export function getComplianceTone(percent) {
  if (percent >= 90) return 'success';
  if (percent >= 70) return 'warning';
  return 'danger';
}

const RING_VIEWBOX = 36;
const RING_CENTER = RING_VIEWBOX / 2;
const RING_STROKE = 3;
const RING_RADIUS = (RING_VIEWBOX - RING_STROKE) / 2;

function buildAriaLabel({ isLoading, label, displayValue, arcValue }) {
  if (isLoading) return `${label} loading`;
  if (displayValue != null) return `${displayValue} due this month, ${arcValue}% tested`;
  return `${arcValue}% ${label.toLowerCase()}`;
}

export default function ComplianceRing({
  percent,
  value,
  label = 'Compliance',
  size = 'md',
  className = '',
  ...props
}) {
  const numeric = Number(percent);
  const isLoading = percent == null || Number.isNaN(numeric);
  const arcValue = isLoading ? 0 : Math.min(100, Math.max(0, Math.round(numeric)));
  const hasDisplayValue = value != null && value !== '';
  const displayNumeric = Number(value);
  const displayValue = hasDisplayValue && !Number.isNaN(displayNumeric)
    ? Math.max(0, Math.round(displayNumeric))
    : null;
  const isEmptyWorkload = displayValue === 0 && arcValue === 0;
  const tone = isLoading || isEmptyWorkload ? 'muted' : getComplianceTone(arcValue);
  const centreText = isLoading
    ? '…'
    : displayValue != null
      ? String(displayValue)
      : `${arcValue}%`;

  const classes = ['compliance-ring', `compliance-ring--${size}`, className]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      data-tone={tone}
      data-percent={arcValue}
      role="img"
      aria-label={buildAriaLabel({ isLoading, label, displayValue, arcValue })}
      {...props}
    >
      <svg
        className="compliance-ring-svg"
        viewBox={`0 0 ${RING_VIEWBOX} ${RING_VIEWBOX}`}
        aria-hidden="true"
      >
        <circle
          className="compliance-ring-bg"
          cx={RING_CENTER}
          cy={RING_CENTER}
          r={RING_RADIUS}
        />
        {arcValue > 0 && (
          <circle
            className="compliance-ring-progress"
            cx={RING_CENTER}
            cy={RING_CENTER}
            r={RING_RADIUS}
            pathLength="100"
            strokeDasharray={`${arcValue} 100`}
            strokeLinecap="round"
            transform={`rotate(-90 ${RING_CENTER} ${RING_CENTER})`}
          />
        )}
      </svg>
      <div className="compliance-ring-inner">
        <span className="compliance-ring-value">{centreText}</span>
        <span className="compliance-ring-caption">{label}</span>
      </div>
    </div>
  );
}
